import { DetailAlbumDto, DetailCreatorDto } from "@/lib/lutely-api/gen/dtos";
import { albumJsonLd, creatorJsonLd } from "@/lib/seo-music";
import { albumPath, breadcrumbJsonLd, creatorPath, seoHead } from "@/lib/seo";

/**
 * Shared head for the two routes that render an artist profile: the canonical
 * `/@slug` and the legacy `/creators/{uuid}`. Both point their canonical at
 * `/@slug` so the duplicate consolidates into one indexed URL.
 */
export const CREATOR_ROUTE_DETAIL = {
  head: ({
    loaderData,
  }: {
    loaderData?: { creator: DetailCreatorDto | null };
  }) => {
    const creator = loaderData?.creator;
    const title = creator?.stageName
      ? `${creator.stageName} | Lutely`
      : "Artist Profile | Lutely";
    const description =
      creator?.bio ||
      "Discover this creator's music, beats, albums, and soundkits on Lutely.";
    const image = creator?.profilePictureUrl || creator?.bannerImageUrl;

    return seoHead({
      title,
      description,
      type: "profile",
      image,
      // Without a slug there is no canonical URL to point at — the uuid route
      // is the duplicate, not the target — so the page stays out of the index.
      canonicalPath: creator?.slug ? creatorPath(creator.slug) : undefined,
      noindex: !creator?.slug,
      jsonLd: creator
        ? [
            creatorJsonLd(creator),
            creator.slug && creator.stageName
              ? breadcrumbJsonLd([
                  { name: "Artists", path: "/artists" },
                  {
                    name: creator.stageName,
                    path: creatorPath(creator.slug),
                  },
                ])
              : null,
          ]
        : null,
    });
  },
};

/**
 * Shared head for the two routes that render a release: the canonical
 * `/@artist/release` and the legacy `/albums/{uuid}`.
 */
export const ALBUM_ROUTE_DETAIL = {
  head: ({ loaderData }: { loaderData?: { album: DetailAlbumDto | null } }) => {
    const album = loaderData?.album;
    const title = album?.title
      ? `${album.creator?.stageName ? `${album.creator.stageName} - ` : ""}${album.title} | Lutely`
      : "Album | Lutely";
    const description =
      album?.description || "Listen to and purchase this album on Lutely.";

    const creatorSlug = album?.creator?.slug;
    const canonicalPath =
      album?.slug && creatorSlug
        ? albumPath(creatorSlug, album.slug)
        : undefined;

    return seoHead({
      title,
      description,
      type: "music.album",
      image: album?.artworkUrl,
      canonicalPath,
      // Unpublished releases are reachable by their owner but must never be
      // indexed, and a release without slugs has no canonical URL to point at.
      noindex: !canonicalPath || !album?.publishedAt,
      jsonLd: album
        ? [
            albumJsonLd(album),
            canonicalPath && album.title
              ? breadcrumbJsonLd([
                  ...(album.creator?.stageName && creatorSlug
                    ? [
                        {
                          name: album.creator.stageName,
                          path: creatorPath(creatorSlug),
                        },
                      ]
                    : []),
                  { name: album.title, path: canonicalPath },
                ])
              : null,
          ]
        : null,
    });
  },
};
